import { userClient } from "@/shared/rpc/clients";
import { userSettingsStore } from "@/shared/stores/UserSettingsStore";

type SettingIdRef = { current: string | null };

const saveQueues = new WeakMap<SettingIdRef, Promise<void>>();

/**
 * Create-or-update a user setting and mirror the result into the settings store.
 * `settingIdRef.current` holds the id of the persisted entry (or null when it
 * has not been created yet). If the saved id is stale (NOT_FOUND / ALREADY_EXISTS)
 * the entry is recreated. Failures are swallowed — preferences are best-effort.
 */
export async function saveUserSetting(
  section: string,
  key: string,
  value: Record<string, unknown>,
  settingIdRef: SettingIdRef,
): Promise<void> {
  const previousSave = saveQueues.get(settingIdRef) ?? Promise.resolve();
  const currentSave = previousSave.then(() => persistUserSetting(section, key, value, settingIdRef));
  saveQueues.set(settingIdRef, currentSave);

  await currentSave;
  if (saveQueues.get(settingIdRef) === currentSave) saveQueues.delete(settingIdRef);
}

async function persistUserSetting(
  section: string,
  key: string,
  value: Record<string, unknown>,
  settingIdRef: SettingIdRef,
): Promise<void> {
  const savedId = settingIdRef.current;
  try {
    const result = savedId
      ? await userClient.updateSetting({ id: savedId, section, key, value: value as any })
      : await userClient.createSetting({ section, key, value: value as any });
    settingIdRef.current = result.id;
    userSettingsStore.setSetting(result);
  } catch (error) {
    const code = (error as { code?: number } | null)?.code;
    if (savedId && (code === 5 || code === 6)) {
      try {
        settingIdRef.current = null;
        const result = await userClient.createSetting({ section, key, value: value as any });
        settingIdRef.current = result.id;
        userSettingsStore.setSetting(result);
      } catch {
        // silent
      }
    }
  }
}